import A11yDialog from 'a11y-dialog';
import './index.scss';

/* DIALOG */
export class Dialog {
  constructor({
    id,
    type,
    ariaLabel,
    title,
    headerHTML,
    appendToBody,
    content,
    overlay,
    scrollblocker,
    appendSelector
  }) {
    const isHTMLElement = content instanceof HTMLElement;
    const parser = new DOMParser();

    const html = parser.parseFromString(
      templateModal({ id, type, ariaLabel, title, headerHTML, overlay }),
      'text/html'
    );

    let parentReference = null;

    if (isHTMLElement) {
      // POUR AJOUTER LE CONTENU SANS RÉECRIRE LE DOM (pour les eventListeners)
      const fragment = document.createDocumentFragment();
      for (const child of Array.from(content.children)) {
        fragment.appendChild(child);
      }
      html.querySelector('.f-dialog__body').appendChild(fragment);
    } else {
      html.querySelector('.f-dialog__body').innerHTML = content;
    }

    this.element = html.querySelector('.f-dialog');
    this.dialog = new A11yDialog(this.element);

    if (id && document.querySelector(`[data-a11y-dialog-show="${id}"]`)) {
      document
        .querySelector(`[data-a11y-dialog-show="${id}"]`)
        .addEventListener('click', (event) => event.preventDefault());
    }

    /* SHOW */
    this.dialog.on('show', () => {
      if (appendToBody) {
        document.body.appendChild(this.element);
      }

      if (appendSelector) {
        // Use appendSelector as a selector to fetch content to move into the dialog
        const selectorContent = typeof appendSelector === 'string' ? document.querySelector(appendSelector) : null;
        if (selectorContent instanceof HTMLElement) {
          // Remember the original parent to restore on hide
          parentReference = selectorContent.parentElement;

          const fragment = document.createDocumentFragment();
          for (const child of Array.from(parentReference.childNodes)) {
            fragment.appendChild(child);
          }
          this.element.querySelector('.f-dialog__body').appendChild(fragment);
        }
      }

      if (id) {
        document.dispatchEvent(new CustomEvent('dialog:show', { detail: { dialogId: id } }));
      }

      if (scrollblocker) {
        document.body.style.marginRight = window.innerWidth - document.body.clientWidth + 'px';
        document.body.style.overflowY = 'hidden';
      }
    });

    /* HIDE */
    this.dialog.on('hide', () => {
      if (appendToBody) {
        content.appendChild(this.element);
      }

      if (id) {
        document.dispatchEvent(new CustomEvent('dialog:hide', { detail: { dialogId: id } }));
      }

      // Restore moved nodes back to the original container
      if (appendSelector && parentReference) {
        const body = this.element.querySelector('.f-dialog__body');
        if (body && parentReference instanceof HTMLElement) {
          const fragment = document.createDocumentFragment();
          for (const child of Array.from(body.childNodes)) {
            fragment.appendChild(child);
          }
          parentReference.appendChild(fragment);
        }
        parentReference = null;
      }

      if (!isHTMLElement) {
        this.element.remove();
      }

      if (scrollblocker) {
        document.body.style.marginRight = '';
        document.body.style.overflowY = '';
      }
    });

    if (isHTMLElement) {
      content.appendChild(this.element);
      content.removeAttribute('aria-hidden');
    } else {
      document.body.appendChild(this.element);
      this.dialog.show();
    }
  }
}

const templateModal = ({ id, ariaLabel, type, title, headerHTML, overlay }) => {
  return `
    <div
      ${id ? `id="${id}"` : ''}
      class="f-dialog${type != '' && type != null ? ' f-dialog--' + type.split(',').join(' f-dialog--').trim() : ' f-dialog--default'}"
      ${ariaLabel ? `aria-label="${ariaLabel}"` : ''}
      aria-hidden="true"
      ${type == '' ? 'data-automation-id="panel-default"' : `data-automation-id="panel-${type}"`}
      role="dialog"
    >
      ${overlay ? '<div class="f-dialog__overlay" data-a11y-dialog-hide></div>' : ''}
      <div class="f-dialog__content">
        <header class="f-dialog__header">
          ${title ? `<p class="f-dialog__title" data-automation-id="panel-cart-title">${title}</p>` : ''}
          ${headerHTML ?? ''}
          <button
            type="button"
            data-a11y-dialog-hide
            class="f-dialog__close"
            title="Fermer la fenêtre"
            data-automation-id="panel-close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="f-icon" fill="currentColor">
              <path d="M20.414 5 19 3.586l-7 7-7-7L3.586 5l7 7-7 7L5 20.414l7-7 7 7L20.414 19l-7-7 7-7z"></path>
            </svg>
          </button>
        </header>
        <div class="f-dialog__body" role="document"></div>
      </div>
    </div>`;
};

/*
  HTMLElement

  <f-dialog>
    <p>contentHTML</p>
  </f-dialog>
*/
export class DialogHTMLElement extends HTMLElement {
  constructor() {
    super();

    this.instance = new Dialog({
      id: this.getAttribute('dialog-id'),
      type: this.getAttribute('dialog-type'),
      title: this.getAttribute('dialog-title'),
      headerHTML: this.getAttribute('dialog-header-html'),
      ariaLabel: this.getAttribute('dialog-aria-label'),
      appendToBody: this.hasAttribute('append-to-body'),
      overlay: this.getAttribute('overlay') !== 'false',
      scrollblocker: this.getAttribute('scrollblocker') !== 'false',
      content: this,
      appendSelector: this.getAttribute('append-selector')
    });
  }
}

customElements.get('f-dialog') || customElements.define('f-dialog', DialogHTMLElement);
